/**
 * Shop Scene: displays available items for purchase using persistent coins.
 *
 * Players browse 12 shop items in a scrollable list, see prices, descriptions,
 * and owned counts. Items are purchased with persistent coins (wallet)
 * accumulated across all playthroughs. Items are sorted alphabetically.
 *
 * Navigable with UP/DOWN arrows, mouse wheel scroll, ENTER/SPACE to buy,
 * and ESC to return.
 */

import Phaser from 'phaser';
import {
  SCENE_SHOP,
  SCENE_TITLE,
  ShopItemType,
  ItemCategory,
  SHOP_ITEM_NAMES,
  SHOP_ITEM_DESCRIPTIONS,
  SHOP_ITEM_PRICES,
  SHOP_ITEM_MAX,
  SHOP_ITEM_TEXTURES,
  SHOP_ITEM_CATEGORIES,
} from '../utils/constants.js';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  COLOR_WHITE,
  BORDER_MARGIN,
  AUDIO_MENU_CLICK,
  AUDIO_MENU_SELECT,
  AUDIO_SHOP_BUY,
  AUDIO_SHOP_DENY,
  AUDIO_ESC_PRESSED,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';
import {
  getWallet,
  purchaseItem,
  getItemCount,
  canPurchaseItem,
  isRingSuppressedByElemental,
  getItemDurability,
  getMaxDurability,
  getItemCategory,
} from '../systems/ShopManager.js';

// --- Layout constants ---
const LIST_X = 70;                // left edge of item list
const ROW_HEIGHT = 56;           // px per item row (enough for wrapped desc)
const ICON_SIZE = 28;            // rendered icon size
const ICON_X = LIST_X;           // icon left edge
const NAME_X = LIST_X + 38;      // name text left edge
const PRICE_X = GAME_WIDTH - 70; // price text right area
const DESC_Y_OFFSET = 17;        // description line offset below name
const DESC_MAX_WIDTH = PRICE_X - NAME_X - 30; // max description width before price col

// --- Scroll area constants ---
const SCROLL_TOP = 95;            // y where the scrollable area begins
const SCROLL_BOTTOM = GAME_HEIGHT - 48; // y where the scrollable area ends (above footer)
const SCROLL_VISIBLE_H = SCROLL_BOTTOM - SCROLL_TOP; // visible height of scroll area
const SCROLL_STEP = 40;           // pixels per scroll step (wheel / arrows)

// --- Text styles ---
const HEADER_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '22px',
  color: '#ffffff',
  letterSpacing: 2,
};

const WALLET_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '13px',
  color: '#cccccc',
};

const ITEM_NAME_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '13px',
  color: '#ffffff',
};

const ITEM_NAME_OWNED_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '13px',
  color: '#888888',
};

const ITEM_NAME_MAXED_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '13px',
  color: '#555555',
};

const ITEM_DESC_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#aaaaaa',
};

const ITEM_PRICE_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '13px',
  color: '#ffd700',
};

const ITEM_PRICE_CANT_AFFORD_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '13px',
  color: '#8b6914',
};

const ITEM_OWNED_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#666666',
};

const ITEM_PROTECTED_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#557755',
};

const ITEM_DURABILITY_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#888888',
};

const FOOTER_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#888888',
};

const TOAST_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#ffffff',
};

/**
 * The ordered list of shop items for display.
 * @type {string[]}
 */
const SHOP_ORDER = [
  ShopItemType.AGMUTH_PENDANT,
  ShopItemType.ELEMENTAL_RING,
  ShopItemType.HAMMER_OF_THREIA,
  ShopItemType.HEART_PENDANT,
  ShopItemType.HEART_POTION,
  ShopItemType.KEY_COPPER,
  ShopItemType.KEY_GOLD,
  ShopItemType.KEY_METALLIC,
  ShopItemType.KEY_PENDANT,
  ShopItemType.LAVA_RING,
  ShopItemType.POISON_RING,
  ShopItemType.TEAR_OF_THREIA,
  ShopItemType.THREIAS_GUIDE,
  ShopItemType.TOTEM_VORACIOUS,
  ShopItemType.WATER_RING,
];

export default class ShopScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENE_SHOP });
  }

  create() {
    this.cameras.main.setBackgroundColor('#000000');
    this.cameras.main.fadeIn(300, 0, 0, 0);

    this._transitioning = false;

    /** @type {number} Currently selected item index. */
    this._selectedIndex = 0;

    /** @type {Phaser.GameObjects.Text} */
    this._walletText = null;

    /** @type {Array<Object>} Per-row display objects. */
    this._rows = [];

    /** @type {Phaser.GameObjects.Text} */
    this._selectorArrow = null;

    /** @type {Phaser.GameObjects.Text|null} Toast message */
    this._toastText = null;
    this._toastTween = null;

    /** @type {Phaser.GameObjects.Container} Scroll container for the item list. */
    this._scrollContainer = null;

    /** @type {number} Current scroll offset (0 = top). */
    this._scrollY = 0;

    /** @type {number} Maximum scroll offset. */
    this._maxScroll = 0;

    this._createHeader();
    this._createScrollContainer();
    this._createItemList();
    this._createSelector();
    this._computeScroll();
    this._createFooter();
    this._drawBorder();
    this._setupInput();
  }

  // ---------------------------------------------------------------
  // UI Creation
  // ---------------------------------------------------------------

  /** @private */
  _createHeader() {
    const cx = GAME_WIDTH / 2;

    // Title
    const title = this.add.text(cx, 38, 'SHOP', HEADER_STYLE).setOrigin(0.5);
    title.setAlpha(0);
    this.tweens.add({
      targets: title,
      alpha: 1,
      duration: 300,
      delay: 50,
      ease: 'Quad.easeOut',
    });

    // Decorative line
    const line = this.add.graphics();
    line.lineStyle(1, COLOR_WHITE, 0.3);
    line.lineBetween(cx - 100, 58, cx + 100, 58);
    line.setAlpha(0);
    this.tweens.add({
      targets: line,
      alpha: 1,
      duration: 300,
      delay: 100,
      ease: 'Quad.easeOut',
    });

    // Wallet display
    const walletAmount = getWallet();
    this._walletText = this.add.text(cx, 74, `Coins: ${walletAmount}`, WALLET_STYLE)
      .setOrigin(0.5);
    this._walletText.setAlpha(0);
    this.tweens.add({
      targets: this._walletText,
      alpha: 1,
      duration: 300,
      delay: 150,
      ease: 'Quad.easeOut',
    });
  }

  /**
   * Creates the scrollable container and its clipping mask.
   * @private
   */
  _createScrollContainer() {
    this._scrollContainer = this.add.container(0, SCROLL_TOP);

    // Geometry mask — only content within the scroll area is visible
    const maskShape = this.make.graphics({ x: 0, y: 0, add: false });
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(0, SCROLL_TOP, GAME_WIDTH, SCROLL_VISIBLE_H);
    const geoMask = maskShape.createGeometryMask();
    this._scrollContainer.setMask(geoMask);
  }

  /**
   * Compute the maximum scroll offset based on total content height.
   * @private
   */
  _computeScroll() {
    const contentH = SHOP_ORDER.length * ROW_HEIGHT;
    this._maxScroll = Math.max(0, contentH - SCROLL_VISIBLE_H);
  }

  /** @private */
  _createItemList() {
    this._rows = [];

    SHOP_ORDER.forEach((itemId, index) => {
      // y relative to the scroll container (0 = first row)
      const y = index * ROW_HEIGHT;
      const row = this._createItemRow(itemId, y, index);
      this._rows.push(row);
    });
  }

  /**
   * Create a single item row with icon, name, description, price, and status.
   * All elements are added to the scroll container.
   * @private
   * @param {string} itemId
   * @param {number} y - Local y within the scroll container
   * @param {number} index
   * @returns {Object}
   */
  _createItemRow(itemId, y, index) {
    const wallet = getWallet();
    const price = SHOP_ITEM_PRICES[itemId];
    const maxQty = SHOP_ITEM_MAX[itemId];
    const owned = getItemCount(itemId);
    const canAfford = wallet >= price;
    const category = getItemCategory(itemId);
    const isSuppressed = isRingSuppressedByElemental(itemId);
    const isMaxed = owned >= maxQty;
    const textureKey = SHOP_ITEM_TEXTURES[itemId];
    const purchaseCheck = canPurchaseItem(itemId);

    // Determine if item is greyed out (unpurchasable for permanent/suppressed reasons)
    const isGreyed = isMaxed || (category === ItemCategory.PERMANENT && owned > 0) || isSuppressed;

    // Icon sprite
    const icon = this.add.sprite(ICON_X + ICON_SIZE / 2, y + ICON_SIZE / 2 - 2, textureKey);
    icon.setDisplaySize(ICON_SIZE, ICON_SIZE);
    if (isGreyed) {
      icon.setAlpha(0.35);
    }
    this._scrollContainer.add(icon);

    // Item name
    const nameStyle = isGreyed ? ITEM_NAME_MAXED_STYLE
      : owned > 0 ? ITEM_NAME_OWNED_STYLE
        : ITEM_NAME_STYLE;
    const nameLabel = SHOP_ITEM_NAMES[itemId];
    const nameText = this.add.text(NAME_X, y - 2, nameLabel, nameStyle);
    this._scrollContainer.add(nameText);

    // Description (word-wrapped to avoid overlapping the price column)
    const descText = this.add.text(NAME_X, y + DESC_Y_OFFSET - 2,
      SHOP_ITEM_DESCRIPTIONS[itemId],
      { ...ITEM_DESC_STYLE, wordWrap: { width: DESC_MAX_WIDTH, useAdvancedWrap: true } });
    this._scrollContainer.add(descText);

    // Price/status area
    let priceText;
    let durabilityText = null;

    if (isSuppressed) {
      priceText = this.add.text(PRICE_X, y, 'PROTECTED', ITEM_PROTECTED_STYLE).setOrigin(0.5, 0);
    } else if (category === ItemCategory.PERMANENT && owned > 0) {
      priceText = this.add.text(PRICE_X, y, 'OWNED', ITEM_OWNED_STYLE).setOrigin(0.5, 0);
    } else if (category === ItemCategory.DURABILITY && owned > 0) {
      const charges = getItemDurability(itemId);
      const maxCharges = getMaxDurability(itemId);
      if (charges > 0) {
        priceText = this.add.text(PRICE_X, y - 2, 'ACTIVE', ITEM_OWNED_STYLE).setOrigin(0.5, 0);
        durabilityText = this.add.text(PRICE_X, y + 12,
          `${charges}/${maxCharges}`, ITEM_DURABILITY_STYLE).setOrigin(0.5, 0);
        this._scrollContainer.add(durabilityText);
      } else {
        const priceStyle = canAfford ? ITEM_PRICE_STYLE : ITEM_PRICE_CANT_AFFORD_STYLE;
        priceText = this.add.text(PRICE_X, y, `${price}c`, priceStyle).setOrigin(0.5, 0);
      }
    } else if (isMaxed) {
      const maxLabel = maxQty > 1 ? `OWNED x${owned}` : 'OWNED';
      priceText = this.add.text(PRICE_X, y, maxLabel, ITEM_OWNED_STYLE).setOrigin(0.5, 0);
    } else {
      const priceStyle = canAfford ? ITEM_PRICE_STYLE : ITEM_PRICE_CANT_AFFORD_STYLE;
      let priceLabel = `${price}c`;
      if (owned > 0 && maxQty > 1) {
        priceLabel += ` (${owned}/${maxQty})`;
      }
      priceText = this.add.text(PRICE_X, y, priceLabel, priceStyle).setOrigin(0.5, 0);
    }
    this._scrollContainer.add(priceText);

    // Owned indicator dots (for consumable multi-quantity items only)
    const dots = [];
    if (category === ItemCategory.CONSUMABLE && maxQty > 1 && owned > 0 && !isMaxed) {
      for (let i = 0; i < owned; i++) {
        const dot = this.add.graphics();
        dot.fillStyle(0xffffff, 0.5);
        dot.fillCircle(PRICE_X - 8 + i * 10, y + 22, 2);
        dots.push(dot);
        this._scrollContainer.add(dot);
      }
    }

    // Entrance animation — staggered fade-in
    const elements = [icon, nameText, descText, priceText, ...dots];
    if (durabilityText) elements.push(durabilityText);
    elements.forEach(el => el.setAlpha(0));
    this.tweens.add({
      targets: elements,
      alpha: { getStart: () => 0, getEnd: (target) => {
        if (target === icon && isGreyed) return 0.35;
        return target.alpha || 1;
      }},
      duration: 200,
      delay: 200 + index * 60,
      ease: 'Quad.easeOut',
    });

    // Separator line
    if (index < SHOP_ORDER.length - 1) {
      const sep = this.add.graphics();
      sep.lineStyle(1, COLOR_WHITE, 0.08);
      sep.lineBetween(LIST_X - 10, y + ROW_HEIGHT - 4, GAME_WIDTH - LIST_X + 10, y + ROW_HEIGHT - 4);
      sep.setAlpha(0);
      this.tweens.add({
        targets: sep,
        alpha: 1,
        duration: 200,
        delay: 200 + index * 60,
        ease: 'Quad.easeOut',
      });
      this._scrollContainer.add(sep);
    }

    // Mouse interaction — use nameText as the primary click target for the row
    nameText.setInteractive({ useHandCursor: true });
    nameText.on('pointerover', () => {
      if (this._transitioning) return;
      if (this._selectedIndex !== index) {
        this._selectedIndex = index;
        try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.4) }); } catch (_) { /* */ }
        this._positionSelector(true);
        this._ensureVisible();
      }
    });
    nameText.on('pointerdown', () => {
      if (this._transitioning) return;
      this._selectedIndex = index;
      this._positionSelector(false);
      this._tryPurchase();
    });

    // Icon is also clickable (larger hit area)
    icon.setInteractive({ useHandCursor: true });
    icon.on('pointerover', () => {
      if (this._transitioning) return;
      if (this._selectedIndex !== index) {
        this._selectedIndex = index;
        try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.4) }); } catch (_) { /* */ }
        this._positionSelector(true);
        this._ensureVisible();
      }
    });
    icon.on('pointerdown', () => {
      if (this._transitioning) return;
      this._selectedIndex = index;
      this._positionSelector(false);
      this._tryPurchase();
    });

    return {
      itemId,
      icon,
      nameText,
      descText,
      priceText,
      durabilityText,
      dots,
      y,
      isMaxed,
      isGreyed,
      canAfford,
    };
  }

  /** @private */
  _createSelector() {
    const firstRow = this._rows[0];
    this._selectorArrow = this.add.text(
      LIST_X - 22,
      firstRow.y + 4,
      '>',
      { ...ITEM_NAME_STYLE, fontSize: '16px' },
    ).setOrigin(0.5);
    this._scrollContainer.add(this._selectorArrow);

    this._selectorArrow.setAlpha(0);
    this.tweens.add({
      targets: this._selectorArrow,
      alpha: 1,
      duration: 200,
      delay: 200 + SHOP_ORDER.length * 60,
      ease: 'Quad.easeOut',
    });
  }

  /** @private */
  _createFooter() {
    const cx = GAME_WIDTH / 2;

    const footer = this.add.text(cx, GAME_HEIGHT - 32,
      'UP/DOWN navigate  |  ENTER buy  |  ESC back', FOOTER_STYLE)
      .setOrigin(0.5);
    footer.setAlpha(0);
    this.tweens.add({
      targets: footer,
      alpha: 1,
      duration: 300,
      delay: 600,
      ease: 'Quad.easeOut',
    });
  }

  /** @private */
  _drawBorder() {
    const g = this.add.graphics();
    g.lineStyle(1, COLOR_WHITE, 0.2);
    g.strokeRect(BORDER_MARGIN, BORDER_MARGIN,
      GAME_WIDTH - BORDER_MARGIN * 2, GAME_HEIGHT - BORDER_MARGIN * 2);

    // Inner decorative corners
    const cornerSize = 10;
    const m = BORDER_MARGIN + 5;
    g.lineStyle(1, COLOR_WHITE, 0.4);

    g.lineBetween(m, m, m + cornerSize, m);
    g.lineBetween(m, m, m, m + cornerSize);
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m - cornerSize, m);
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m, m + cornerSize);
    g.lineBetween(m, GAME_HEIGHT - m, m + cornerSize, GAME_HEIGHT - m);
    g.lineBetween(m, GAME_HEIGHT - m, m, GAME_HEIGHT - m - cornerSize);
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m - cornerSize, GAME_HEIGHT - m);
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m, GAME_HEIGHT - m - cornerSize);
  }

  // ---------------------------------------------------------------
  // Input
  // ---------------------------------------------------------------

  /** @private */
  _setupInput() {
    this.input.keyboard.on('keydown-UP', () => this._navigate(-1));
    this.input.keyboard.on('keydown-DOWN', () => this._navigate(1));
    this.input.keyboard.on('keydown-ENTER', () => this._tryPurchase());
    this.input.keyboard.on('keydown-SPACE', () => this._tryPurchase());
    this.input.keyboard.on('keydown-ESC', () => this._goBack());

    // Mouse wheel scrolling
    this.input.on('wheel', (_pointer, _gos, _dx, dy) => {
      this._scroll(dy > 0 ? SCROLL_STEP : -SCROLL_STEP);
    });
  }

  /**
   * Scroll the item list by the given delta.
   * Positive = down, negative = up.
   * @private
   * @param {number} delta
   */
  _scroll(delta) {
    this._scrollY = Phaser.Math.Clamp(this._scrollY + delta, 0, this._maxScroll);
    this._scrollContainer.setY(SCROLL_TOP - this._scrollY);
  }

  /**
   * Ensure that the selected row is fully visible in the scroll area.
   * Auto-scrolls if needed.
   * @private
   */
  _ensureVisible() {
    const row = this._rows[this._selectedIndex];
    const rowTop = row.y;                // local y within container
    const rowBottom = row.y + ROW_HEIGHT;

    // If the row's top edge is above the visible window, scroll up
    if (rowTop < this._scrollY) {
      this._scrollY = rowTop;
    }
    // If the row's bottom edge is below the visible window, scroll down
    else if (rowBottom > this._scrollY + SCROLL_VISIBLE_H) {
      this._scrollY = rowBottom - SCROLL_VISIBLE_H;
    }

    this._scrollY = Phaser.Math.Clamp(this._scrollY, 0, this._maxScroll);
    this._scrollContainer.setY(SCROLL_TOP - this._scrollY);
  }

  /** @private */
  _navigate(delta) {
    if (this._transitioning) return;

    try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.4) }); } catch { /* noop */ }

    const count = SHOP_ORDER.length;
    this._selectedIndex = ((this._selectedIndex + delta) % count + count) % count;
    this._positionSelector(true);
    this._ensureVisible();
  }

  /**
   * Move the selector arrow to the current item row.
   * @private
   * @param {boolean} animate
   */
  _positionSelector(animate) {
    const row = this._rows[this._selectedIndex];
    const targetY = row.y + 4;

    // Ensure the arrow is visible
    this._selectorArrow.setAlpha(1);

    if (animate) {
      this.tweens.killTweensOf(this._selectorArrow);
      this.tweens.add({
        targets: this._selectorArrow,
        y: targetY,
        duration: 100,
        ease: 'Quad.easeOut',
      });
    } else {
      this._selectorArrow.setY(targetY);
    }
  }

  // ---------------------------------------------------------------
  // Purchase
  // ---------------------------------------------------------------

  /** @private */
  _tryPurchase() {
    if (this._transitioning) return;

    const itemId = SHOP_ORDER[this._selectedIndex];
    const result = purchaseItem(itemId);

    if (result.success) {
      try { this.sound.play(AUDIO_SHOP_BUY, { volume: getSfxVol(0.6) }); } catch { /* noop */ }
      this._showToast(`Purchased ${SHOP_ITEM_NAMES[itemId]}!`, '#00ff88');
      this._refreshDisplay();
    } else {
      try { this.sound.play(AUDIO_SHOP_DENY, { volume: getSfxVol(0.4) }); } catch { /* noop */ }
      this._showToast(result.reason || 'Cannot purchase', '#ff4444');
      this._shakeSelector();
    }
  }

  /**
   * Show a toast message at the bottom of the screen (fixed, not scrolled).
   * @private
   * @param {string} message
   * @param {string} color
   */
  _showToast(message, color) {
    if (this._toastTween) {
      this._toastTween.destroy();
      this._toastTween = null;
    }
    if (this._toastText) {
      this._toastText.destroy();
      this._toastText = null;
    }

    // Toast is added to the scene directly, NOT to the scroll container
    this._toastText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 55, message, {
      ...TOAST_STYLE,
      color,
    }).setOrigin(0.5);
    this._toastText.setAlpha(0);

    this._toastTween = this.tweens.add({
      targets: this._toastText,
      alpha: { from: 0, to: 1 },
      duration: 200,
      yoyo: true,
      hold: 1200,
      ease: 'Quad.easeOut',
      onComplete: () => {
        if (this._toastText) {
          this._toastText.destroy();
          this._toastText = null;
        }
      },
    });
  }

  /**
   * Shake the selector for denied purchase feedback.
   * @private
   */
  _shakeSelector() {
    const origX = this._selectorArrow.x;
    this.tweens.add({
      targets: this._selectorArrow,
      x: origX + 4,
      duration: 40,
      yoyo: true,
      repeat: 3,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this._selectorArrow.setX(origX);
      },
    });
  }

  /**
   * Refresh all item rows and the wallet display after a purchase.
   * New dynamic elements (durabilityText, dots) are added to the scroll container.
   * @private
   */
  _refreshDisplay() {
    // Update wallet
    const walletAmount = getWallet();
    this._walletText.setText(`Coins: ${walletAmount}`);

    // Refresh each row
    this._rows.forEach((row) => {
      const itemId = row.itemId;
      const wallet = getWallet();
      const price = SHOP_ITEM_PRICES[itemId];
      const maxQty = SHOP_ITEM_MAX[itemId];
      const owned = getItemCount(itemId);
      const canAfford = wallet >= price;
      const category = getItemCategory(itemId);
      const isSuppressed = isRingSuppressedByElemental(itemId);
      const isMaxed = owned >= maxQty;

      const isGreyed = isMaxed || (category === ItemCategory.PERMANENT && owned > 0) || isSuppressed;

      row.isMaxed = isMaxed;
      row.isGreyed = isGreyed;
      row.canAfford = canAfford;

      // Update icon alpha
      row.icon.setAlpha(isGreyed ? 0.35 : 1);

      // Update name style
      const nameStyle = isGreyed ? ITEM_NAME_MAXED_STYLE
        : owned > 0 ? ITEM_NAME_OWNED_STYLE
          : ITEM_NAME_STYLE;
      row.nameText.setStyle(nameStyle);

      // Destroy old durability text if present
      if (row.durabilityText) {
        row.durabilityText.destroy();
        row.durabilityText = null;
      }

      // Update price/status text
      if (isSuppressed) {
        row.priceText.setText('PROTECTED');
        row.priceText.setStyle(ITEM_PROTECTED_STYLE);
        row.priceText.setY(row.y);
      } else if (category === ItemCategory.PERMANENT && owned > 0) {
        row.priceText.setText('OWNED');
        row.priceText.setStyle(ITEM_OWNED_STYLE);
        row.priceText.setY(row.y);
      } else if (category === ItemCategory.DURABILITY && owned > 0) {
        const charges = getItemDurability(itemId);
        const maxCharges = getMaxDurability(itemId);
        if (charges > 0) {
          row.priceText.setText('ACTIVE');
          row.priceText.setStyle(ITEM_OWNED_STYLE);
          row.priceText.setY(row.y - 2);
          row.durabilityText = this.add.text(PRICE_X, row.y + 12,
            `${charges}/${maxCharges}`, ITEM_DURABILITY_STYLE).setOrigin(0.5, 0);
          this._scrollContainer.add(row.durabilityText);
        } else {
          const priceStyle = canAfford ? ITEM_PRICE_STYLE : ITEM_PRICE_CANT_AFFORD_STYLE;
          row.priceText.setText(`${price}c`);
          row.priceText.setStyle(priceStyle);
          row.priceText.setY(row.y);
        }
      } else if (isMaxed) {
        const maxLabel = maxQty > 1 ? `OWNED x${owned}` : 'OWNED';
        row.priceText.setText(maxLabel);
        row.priceText.setStyle(ITEM_OWNED_STYLE);
        row.priceText.setY(row.y);
      } else {
        const priceStyle = canAfford ? ITEM_PRICE_STYLE : ITEM_PRICE_CANT_AFFORD_STYLE;
        let priceLabel = `${price}c`;
        if (owned > 0 && maxQty > 1) {
          priceLabel += ` (${owned}/${maxQty})`;
        }
        row.priceText.setText(priceLabel);
        row.priceText.setStyle(priceStyle);
        row.priceText.setY(row.y);
      }

      // Update owned dots (consumable multi-quantity items only)
      row.dots.forEach(dot => dot.destroy());
      row.dots = [];
      if (category === ItemCategory.CONSUMABLE && maxQty > 1 && owned > 0 && !isMaxed) {
        for (let i = 0; i < owned; i++) {
          const dot = this.add.graphics();
          dot.fillStyle(0xffffff, 0.5);
          dot.fillCircle(PRICE_X - 8 + i * 10, row.y + 22, 2);
          row.dots.push(dot);
          this._scrollContainer.add(dot);
        }
      }
    });
  }

  // ---------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------

  /** @private */
  _goBack() {
    if (this._transitioning) return;
    this._transitioning = true;

    try { this.sound.play(AUDIO_ESC_PRESSED, { volume: getSfxVol(0.4) }); } catch { /* noop */ }

    this.cameras.main.fadeOut(300, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(SCENE_TITLE);
    });
  }
}
